/**
 * Logging utility with environment-based control
 * Provides consistent logging across the application with production-safe error handling
 *
 * Features:
 * - Automatic database logging in production (vzw_logs_system table)
 * - Production-safe error sanitization
 * - Type-safe error handling with PostgrestError support
 * - Console and database dual logging
 * - Rate limiting to prevent log flooding
 * - Server-side and client-side logging support
 * - Retry mechanism for failed log saves
 */

import type { ApiError } from '#shared/types/errors'
import type { LogCategory } from '~/composables/useSupabaseLogs'
import { isPostgrestError, sanitizeError, toApiError } from '#shared/types/errors'

interface LogContext {
  [key: string]: unknown
}

interface DatabaseLogOptions {
  saveToDatabase?: boolean
  category?: LogCategory
  userId?: string
}

class Logger {
  private isDevelopment: boolean
  private isProduction: boolean
  private databaseLoggingEnabled: boolean
  private logCache: Map<string, number> = new Map() // For rate limiting
  private readonly RATE_LIMIT_WINDOW = 60000 // 1 minute
  private readonly MAX_LOGS_PER_WINDOW = 10 // Max same logs per minute

  constructor() {
    // Check if we're in development mode
    this.isDevelopment = process.env.NODE_ENV !== 'production'
    this.isProduction = process.env.NODE_ENV === 'production'

    // Enable database logging in production by default
    this.databaseLoggingEnabled = this.isProduction

    // Periodically clean up rate limit cache (only on client-side)
    if (typeof setInterval !== 'undefined' && import.meta.client) {
      setInterval(() => {
        const now = Date.now()
        for (const [key, timestamp] of this.logCache.entries()) {
          if (now - timestamp > this.RATE_LIMIT_WINDOW)
            this.logCache.delete(key)
        }
      }, this.RATE_LIMIT_WINDOW)
    }
  }

  /**
   * Check if a log should be rate-limited
   */
  private shouldRateLimit(message: string, level: string): boolean {
    const key = `${level}:${message}`
    const now = Date.now()
    const lastLogged = this.logCache.get(key)

    if (!lastLogged) {
      this.logCache.set(key, now)

      return false
    }

    if (now - lastLogged < this.RATE_LIMIT_WINDOW) {
      // Count how many times this was logged in the window
      const count = Array.from(this.logCache.entries())
        .filter(([k, t]) => k === key && now - t < this.RATE_LIMIT_WINDOW)
        .length

      if (count >= this.MAX_LOGS_PER_WINDOW)
        return true
    }

    this.logCache.set(key, now)

    return false
  }

  /**
   * Save log to database (vzw_logs_system table)
   * Only called in production or when explicitly enabled
   * Supports both client-side and client-side logging
   */
  private async saveToDatabase(
    level: 'error' | 'warning' | 'info',
    message: string,
    error?: unknown,
    context?: LogContext & DatabaseLogOptions,
    retryCount = 0,
  ): Promise<void> {
    // Skip if database logging is disabled
    if (!this.databaseLoggingEnabled)
      return

    // Skip if explicitly disabled for this log
    if (context?.saveToDatabase === false)
      return

    try {
      const isClientSide = typeof window !== 'undefined'

      if (isClientSide) {
        // Client-side: Use composable
        const { useSupabaseLogs } = await import('~/composables/useSupabaseLogs')
        const { createLog } = useSupabaseLogs()

        await createLog({
          level: level === 'warning' ? 'warning' : level === 'error' ? 'error' : 'info',
          category: context?.category || 'system',
          message,
          details: context ? { ...context, saveToDatabase: undefined, category: undefined, userId: undefined } : undefined,
          error: error instanceof Error ? error : undefined,
        })
      }
      else {
        // Server-side: Use server-side API call
        try {
          const logData = {
            level: level === 'warning' ? 'warning' : level === 'error' ? 'error' : 'info',
            category: context?.category || 'system',
            message,
            details: context ? { ...context, saveToDatabase: undefined, category: undefined, userId: undefined } : undefined,
            error_message: error instanceof Error ? error.message : String(error),
            error_name: error instanceof Error ? error.name : undefined,
            error_stack: error instanceof Error && this.isDevelopment ? error.stack : undefined,
            user_id: context?.userId || null,
          }

          // Use $fetch for server-side API call
          await $fetch('/api/logs/system', {
            method: 'POST',
            body: logData,
          })
        }
        catch (serverError) {
          // Fallback: store in pending logs for retry
          if (this.isDevelopment)
            console.error('Server-side log save failed:', serverError)
        }
      }
    }
    catch (dbError) {
      // Retry logic for transient failures
      if (retryCount < 2) {
        setTimeout(() => {
          this.saveToDatabase(level, message, error, context, retryCount + 1).catch(() => {
            // Final failure - just log to console in dev
            if (this.isDevelopment)
              console.error('Failed to save log after retries:', dbError)
          })
        }, 1000 * (retryCount + 1)) // Exponential backoff
      }
      else {
        // Fail silently after retries - don't break app
        if (this.isDevelopment)
          console.error('Failed to save log to database after retries:', dbError)
      }
    }
  }

  /**
   * Log debug information (only in development)
   */
  debug(message: string, context?: LogContext): void {
    if (this.isDevelopment)
      console.log(`🔍 [DEBUG] ${message}`, context || '')
  }

  /**
   * Log informational messages
   */
  info(message: string, context?: LogContext): void {
    if (this.isDevelopment)
      console.log(`ℹ️ [INFO] ${message}`, context || '')
  }

  /**
   * Log warning messages with rate limiting
   */
  warn(message: string, context?: LogContext): void {
    // Rate limit warnings
    if (this.shouldRateLimit(message, 'warn'))
      return

    console.warn(`⚠️ [WARN] ${message}`, context || '')
  }

  /**
   * Log error messages with production-safe sanitization
   * In production: removes stack traces and sensitive details
   * In development: includes full error information
   * Automatically saves to database in production with rate limiting
   */
  error(message: string, error?: Error | unknown, context?: LogContext & DatabaseLogOptions): void {
    // Rate limit identical errors to prevent flooding
    if (this.shouldRateLimit(message, 'error')) {
      if (this.isDevelopment)
        console.warn('⚠️ Error rate limited:', message)

      return
    }

    const sanitized = sanitizeError(error, this.isProduction)

    console.error(`❌ [ERROR] ${message}`, {
      error: sanitized,
      ...context,
    })

    // Save to database in production (async, non-blocking)
    if (this.databaseLoggingEnabled) {
      this.saveToDatabase('error', message, error, context).catch(() => {
        // Ignore database logging errors
      })
    }
  }

  /**
   * Log API errors with structured information
   * Automatically converts errors to ApiError format and saves to database
   */
  apiError(endpoint: string, error: Error | unknown, context?: LogContext & DatabaseLogOptions): void {
    const apiError: ApiError = toApiError(error, 'API request failed')

    this.error(`API Error: ${endpoint}`, error, {
      endpoint,
      statusCode: apiError.statusCode,
      code: apiError.code,
      category: 'api_error',
      timestamp: new Date().toISOString(),
      ...context,
    })
  }

  /**
   * Log database errors with PostgrestError type handling
   * Automatically saves to database with 'database' category
   */
  dbError(operation: string, error: Error | unknown, context?: LogContext & DatabaseLogOptions): void {
    // Include additional context for Postgrest errors
    const extraContext = isPostgrestError(error)
      ? { code: error.code, hint: this.isDevelopment ? error.hint : undefined, details: this.isDevelopment ? error.details : undefined }
      : {}

    this.error(`Database Error: ${operation}`, error, {
      operation,
      category: 'database_error',
      timestamp: new Date().toISOString(),
      ...extraContext,
      ...context,
    })
  }

  /**
   * Log validation errors
   */
  validationError(field: string, message: string, context?: LogContext): void {
    this.warn(`Validation Error: ${field} - ${message}`, context)
  }

  /**
   * Performance monitoring - log slow operations
   */
  performance(operation: string, duration: number, threshold: number = 1000): void {
    if (duration > threshold) {
      this.warn(`Slow operation detected: ${operation} took ${duration}ms`, {
        operation,
        duration,
        threshold,
      })
    }
  }
}

// Export singleton instance
export const logger = new Logger()

// Convenience exports using arrow functions to avoid bind memory issues
export const logDebug = (message: string, context?: LogContext) => logger.debug(message, context)
export const logInfo = (message: string, context?: LogContext) => logger.info(message, context)
export const logWarn = (message: string, context?: LogContext) => logger.warn(message, context)
export const logError = (message: string, error?: Error | unknown, context?: LogContext & DatabaseLogOptions) => logger.error(message, error, context)
export const logApiError = (endpoint: string, error: Error | unknown, context?: LogContext & DatabaseLogOptions) => logger.apiError(endpoint, error, context)
export const logDbError = (operation: string, error: Error | unknown, context?: LogContext & DatabaseLogOptions) => logger.dbError(operation, error, context)
export const logValidationError = (field: string, message: string, context?: LogContext) => logger.validationError(field, message, context)
export const logPerformance = (operation: string, duration: number, threshold?: number) => logger.performance(operation, duration, threshold)
